import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { getGuideExperiences } from "store/guide-profile"
import { DatePicker, Button, Carousel } from "antd"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import CommonEmptyStateUI from "../CommonEmptyStateUI"
import dayjs from "dayjs"
import EmbeddedExperienceCard from "../../Embedded/ExperienceCard/index"
import styles from "./index.module.css"
import { useTranslation } from "react-i18next"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { Title, Text } from "ui/Typography"
const GuideExperiences = () => {
  const dispatch = useDispatch()
  const { slug } = useParams()
  const { t } = useTranslation()

  const experiences = useSelector((store) => store?.guideProfile?.experiences?.data)
  const loading = useSelector((store) => store?.guideProfile?.experiences?.loading)

  const [open, setOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)

  useEffect(() => {
    if (slug) {
      dispatch(getGuideExperiences(slug, null))
    }
  }, [slug, dispatch])

  const soldOutDates = experiences?.availability?.sold_out_dates?.map((d) => d.date) || []

  const disabledDate = (current) => {
    if (!current) return false
    const isPast = current < dayjs().startOf("day")
    const isSoldOut = soldOutDates.some((d) => current.isSame(dayjs(d, "YYYY-MM-DD"), "day"))
    return isPast || isSoldOut
  }

  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = date.format("YYYY-MM-DD")
      setSelectedDate(formattedDate)
      dispatch(getGuideExperiences(slug, formattedDate))
    } else {
      setSelectedDate(null)
      dispatch(getGuideExperiences(slug, null))
    }
    setOpen(false)
  }

  const clearDate = () => {
    setSelectedDate(null)
    setOpen(false)
    dispatch(getGuideExperiences(slug, null))
  }

  return (
    <FirstLoading loading={loading} name="guideProfile.experiences">
      <div className={styles.guideExperiencesContainer}>
        {Array.isArray(experiences?.trips) && experiences.trips.length > 0 && (
          <div className={styles.experiencesHeader}>
            <Title level={4}>
              {t("guide.navigation.this_guide_proposes_experiences", {
                count: experiences?.trips?.length || 0
              })}
            </Title>
            <div className={styles.dateDropdownWrapper}>
              <Button shape="round" className={styles.dateDropdownBtn} onClick={() => setOpen((prev) => !prev)}>
                <Text className={styles.dateDropdownText}>
                  {selectedDate ? `Date: ${dayjs(selectedDate).format("MMMM D, YYYY")}` : "Date"}
                </Text>
                <Text className={styles.dateDropdownIcon}>▼</Text>
              </Button>
              {open && (
                <div className={styles.dateDropdownPopup}>
                  <DatePicker
                    open={true}
                    onOpenChange={(o) => {
                      if (!o) setOpen(false)
                    }}
                    value={selectedDate ? dayjs(selectedDate, "YYYY-MM-DD") : null}
                    onChange={handleDateChange}
                    disabledDate={disabledDate}
                    allowClear={true}
                    suffixIcon={null}
                    placeholder="Select date"
                    className={styles.datePicker}
                    popupClassName={styles.datePickerDropdown}
                    showNow={false}
                    format="MMMM D, YYYY"
                    key={selectedDate ? "date-selected" : "date-not-selected"}
                    renderExtraFooter={() => (
                      <div className={styles.datePickerFooter}>
                        <Button type="default" onClick={clearDate} className={styles.clearBtn}>
                          Clear
                        </Button>
                      </div>
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.experiencesList}>
          <WithEmptyStateWrapper
            name="experiences"
            hasItems={Array.isArray(experiences?.trips) && experiences?.trips?.length > 0}
            fallback={
              <CommonEmptyStateUI
                titleKey="guide.navigation.no_experiences"
                descKey="guide.navigation.experiences_will_be_displayed_here"
              />
            }
          >
            <div className={styles.carouselContainer}>
              <Carousel
                dotPosition="bottom"
                dots={experiences?.trips?.length > 3 ? { className: styles.dots } : false}
                slidesToShow={Math.min(3, experiences?.trips?.length)}
                slidesToScroll={1}
                infinite={false}
                arrows={false}
                lazyLoad="ondemand"
                swipeToSlide={true}
                draggable={true}
                centerMode={false}
                className={styles.experienceCarousel}
                autoplay={true}
                autoplaySpeed={2000}
                dotActiveWidth={20}
                responsive={[
                  {
                    breakpoint: 1200,
                    settings: {
                      slidesToShow: Math.min(2, experiences?.trips?.length),
                      slidesToScroll: 1
                    }
                  },
                  {
                    breakpoint: 991,
                    settings: {
                      slidesToShow: 1,
                      slidesToScroll: 1,
                      centerMode: true,
                      centerPadding: "40px"
                    }
                  }
                ]}
              >
                {experiences?.trips?.map((exp, index) => {
                  // prefer cropped_photos when present, otherwise fall back to photos by position (position === 0) or first photo
                  const hasCropped = Array.isArray(exp?.cropped_photos) && exp.cropped_photos.length > 0
                // const hasCropped =    Array.isArray(exp?.cropped_photos) ? exp.cropped_photos.find((p) => p?.position === 0) : null
                  const croppedPhoto = hasCropped ? exp.cropped_photos.find((p) => p?.position === 0) : null
                  const positionedPhoto = Array.isArray(exp?.photos) ? exp.photos.find((p) => p?.position === 0) : null
                  const fallbackPhoto = Array.isArray(exp?.photos) && exp.photos.length > 0 ? exp.photos[0] : null
                  const mainPhotoUrl = (croppedPhoto?.preview_url) || (positionedPhoto?.preview_url) || (fallbackPhoto?.preview_url) || null

                  return (
                    <div key={exp?.uuid || index} className={styles.carouselItem}>
                      <EmbeddedExperienceCard
                        image={mainPhotoUrl}
                        // imageIsCropped={Boolean(hasCropped)}
                        title={exp?.title}
                        location={exp?.address}
                        time={exp?.available_hours_range}
                        people={exp?.min_guests}
                        price={exp?.min_price}
                        experienceId={exp?.uuid}
                        showFavoriteButton={true}
                        favorite={exp?.is_favorite}
                        mode="guideProfile"
                      />
                    </div>
                  )
                })}
              </Carousel>
            </div>
          </WithEmptyStateWrapper>
        </div>
      </div>
    </FirstLoading>
  )
}

export default GuideExperiences
